import styled from "@emotion/styled";

type TButtonType = "primary" | "outline" | "secondary";

// Theme configuration for button variants
const BUTTON_THEME = {
  primary: {
    background: "#68ad18",
    color: "#fff",
    border: "none",
    boxShadow: "none",
    iconFill: "#fff",
    hover: {
      background: "#68ad18",
      color: "#fff",
      border: "none",
      boxShadow: "inset 0px 0px 5px rgba(0, 0, 0, 0.3)",
      iconFill: "#fff",
    },
    active: {
      background: "#417900",
      color: "#fff",
      border: "none",
      boxShadow: "inset 0px 0px 5px rgba(0, 0, 0, 0.3)",
      iconFill: "#fff",
    },
    disabled: {
      background: "#F8F8F8",
      color: "#cbd5d8",
      border: "none",
      boxShadow: "none",
      iconFill: "#cbd5d8",
    }
  },
  outline: {
    background: "#fff",
    color: "#68AD18",
    border: "1px solid #68AD18",
    boxShadow: "none",
    iconFill: "#68AD18",
    hover: {
      background: "#F8F8F8",
      color: "#68AD18",
      border: "1px solid #68AD18",
      boxShadow: "none",
      iconFill: "#68AD18",
    },
    active: {
      background: "#F8F8F8",
      color: "#417900",
      border: "1px solid #417900",
      boxShadow: "none",
      iconFill: "#417900",
    },
    disabled: {
      background: "#fff",
      color: "#cbd5d8",
      border: "1px solid #F8F8F8",
      boxShadow: "none",
      iconFill: "#cbd5d8",
    }
  },
  secondary: {
    background: "#fff",
    color: "#494C5B",
    border: "none",
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.16)",
    iconFill: "#494C5B",
    hover: {
      background: "#fff",
      color: "#417900",
      border: "none",
      boxShadow: "none",
      iconFill: "#417900",
    },
    active: {
      background: "#F8F8F8",
      color: "#68AD18",
      border: "none",
      boxShadow: "none",
      iconFill: "#68AD18",
    },
    disabled: {
      background: "#fff",
      color: "#cbd5d8",
      border: "none",
      boxShadow: "none",
      iconFill: "#cbd5d8",
    }
  }
};

const getTheme = (kind: TButtonType = "primary") => BUTTON_THEME[kind] || BUTTON_THEME.primary;

const Button = styled.button<{ kind?: TButtonType }>`
  cursor: pointer;
  outline: none;
  height: 36px;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 14px;
  line-height: 18px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.4s;
  padding: 0 16px;
  box-sizing: border-box;

  /* Default State */
  background: ${({ kind }) => getTheme(kind).background};
  color: ${({ kind }) => getTheme(kind).color};
  border: ${({ kind }) => getTheme(kind).border};
  box-shadow: ${({ kind }) => getTheme(kind).boxShadow};

  & path {
    fill: ${({ kind }) => getTheme(kind).iconFill};
  }

  /* Hover State */
  :hover {
    background: ${({ kind }) => getTheme(kind).hover.background};
    color: ${({ kind }) => getTheme(kind).hover.color};
    border: ${({ kind }) => getTheme(kind).hover.border};
    box-shadow: ${({ kind }) => getTheme(kind).hover.boxShadow};

    & path {
      fill: ${({ kind }) => getTheme(kind).hover.iconFill};
    }
  }

  /* Active State */
  :active {
    background: ${({ kind }) => getTheme(kind).active.background};
    color: ${({ kind }) => getTheme(kind).active.color};
    border: ${({ kind }) => getTheme(kind).active.border};
    box-shadow: ${({ kind }) => getTheme(kind).active.boxShadow};

    & path {
      fill: ${({ kind }) => getTheme(kind).active.iconFill};
    }
  }

  /* Disabled State */
  :disabled {
    cursor: not-allowed;
    background: ${({ kind }) => getTheme(kind).disabled.background};
    color: ${({ kind }) => getTheme(kind).disabled.color};
    border: ${({ kind }) => getTheme(kind).disabled.border};
    box-shadow: ${({ kind }) => getTheme(kind).disabled.boxShadow};

    & path {
      fill: ${({ kind }) => getTheme(kind).disabled.iconFill};
    }
  }
`;

export default Button;
